/**
 * Iter 31 (R1) — EditClientDialog.
 * Edits the three new account configuration fields on a client:
 *   - user_limit_tier  (max collaborators)
 *   - assigned_consultant_id
 *   - suite_codes
 * Bilingual via i18n. Uses adminApi.updateClient + admin/consultants.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import { Textarea } from '@/components/ui/textarea';
import { ShieldCheck } from 'lucide-react';
import { USER_LIMIT_TIER_OPTIONS } from '@/components/admin/NewClientDialog';

const SUITE_CHECKBOXES = [
  { code: 'talentmap',        label: 'TalentMap' },
  { code: 'climara',          label: 'CLIMARA' },
  { code: 'talentcompetency', label: 'TalentCompetency' },
  { code: 'talenttrain',      label: 'TalentTrain' },
  { code: 'talentcheck',      label: 'TalentCheck' },
  { code: 'talentstart',      label: 'TalentStart' },
  { code: 'talentheart',      label: 'TalentHeart' },
  { code: 'talentfix',        label: 'TalentFix' },
  { code: 'intelligence',     label: 'TalentIntelligence' },
  { code: 'workforce_records', label: 'Workforce Records' },
];
// NOTE (UAT B2): Global Ethics is intentionally NOT part of this contracted-suite
// catalog — it is provisioned separately via per-company anonymous access codes.

export default function EditClientDialog({ open, onOpenChange, company, onSaved }) {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const isAdvisor = user?.role === 'hm_advisor';
  const isES = (i18n.language || '').startsWith('es');
  const pick = (en, es) => (isES ? (es || en) : (en || es));
  const [consultants, setConsultants] = useState([]);
  const [form, setForm] = useState({
    user_limit_tier: 'up_to_25',
    assigned_consultant_id: '',
    suite_codes: [],
  });
  const [changeReason, setChangeReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      user_limit_tier: company?.user_limit_tier || 'up_to_25',
      assigned_consultant_id: company?.assigned_consultant_id || '',
      suite_codes: [...(company?.suite_codes || [])],
    });
    adminApi.listConsultants().then(setConsultants).catch(() => setConsultants([]));
  }, [open, company]);

  const toggleSuite = (code) => {
    setForm((p) => ({
      ...p,
      suite_codes: p.suite_codes.includes(code)
        ? p.suite_codes.filter((c) => c !== code)
        : [...p.suite_codes, code],
    }));
  };

  const save = async () => {
    if (!company?.id) return;
    setSaving(true);
    try {
      const body = {
        user_limit_tier: form.user_limit_tier,
        assigned_consultant_id: form.assigned_consultant_id || null,
        suite_codes: form.suite_codes,
      };
      if (isAdvisor && changeReason.trim()) body.change_reason = changeReason.trim();
      const res = await adminApi.updateClient(company.id, body);
      const gov = res.governance || {};
      // Founder rule — the message must distinguish applied / queued / unchanged.
      if ((gov.queued_fields || []).length) {
        toast.success(
          t('governance.queuedToast', { defaultValue: 'Changes submitted for approval' }),
          { description: t('governance.queuedDesc', {
              defaultValue: 'Pending HM Admin review: {{fields}}',
              fields: gov.queued_fields.map((f) => t(`governance.fields.${f}`, { defaultValue: f })).join(', '),
            }) },
        );
      } else if (gov.action === 'cancelled') {
        toast.info(t('governance.requestCancelled', { defaultValue: 'Pending request withdrawn — all changes reverted to current values' }));
      } else if ((gov.applied_fields || []).length || !isAdvisor) {
        toast.success(t('clientProfile.profileSaved', { defaultValue: 'Client profile updated' }));
      } else {
        toast.info(t('governance.nothingChanged', { defaultValue: 'No changes to save' }));
      }
      setChangeReason('');
      onSaved?.(res.company);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'GOVERNANCE_CONFLICT') {
        const c = (detail.conflicts || [])[0] || {};
        toast.error(
          t('governance.conflictTitle', { defaultValue: 'Change already awaiting review' }),
          { description: t('governance.conflictDesc', {
              defaultValue: '{{name}} already has a pending change for: {{fields}}',
              name: c.requested_by_name || '—',
              fields: (c.fields || []).map((f) => t(`governance.fields.${f}`, { defaultValue: f })).join(', '),
            }) },
        );
      } else {
        toast.error(typeof detail === 'string' ? detail : (isES ? 'Error al guardar' : 'Save failed'));
      }
    } finally { setSaving(false); }
  };

  const governedChanged = isAdvisor && (
    form.user_limit_tier !== (company?.user_limit_tier || 'up_to_25')
    || (form.assigned_consultant_id || '') !== (company?.assigned_consultant_id || '')
    || JSON.stringify([...form.suite_codes].sort()) !== JSON.stringify([...(company?.suite_codes || [])].sort())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle>{t('clientProfile.editTitle', { defaultValue: 'Edit client configuration' })}</DialogTitle>
          <DialogDescription>
            {company?.name} — {t('clientProfile.editDesc', { defaultValue: 'Update plan tier, assigned consultant and contracted suites.' })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('clientProfile.userLimit', { defaultValue: 'Maximum users' })} *</Label>
              <Select value={form.user_limit_tier}
                      onValueChange={(v) => setForm({ ...form, user_limit_tier: v })}>
                <SelectTrigger data-testid="ec-user-limit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {USER_LIMIT_TIER_OPTIONS.map((o) => (
                    <SelectItem key={o.code} value={o.code}>{pick(o.label_en, o.label_es)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('clientProfile.assignedConsultant', { defaultValue: 'Assigned consultant' })}</Label>
              <Select value={form.assigned_consultant_id || '__none__'}
                      onValueChange={(v) => setForm({ ...form, assigned_consultant_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="ec-consultant"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('clientProfile.unassigned', { defaultValue: 'Unassigned' })}</SelectItem>
                  {consultants.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>{t('clientProfile.contractedSuites', { defaultValue: 'Contracted suites' })}</Label>
            <div className="grid grid-cols-2 gap-2 mt-2" data-testid="ec-suites">
              {SUITE_CHECKBOXES.map((s) => {
                const checked = form.suite_codes.includes(s.code);
                return (
                  <label key={s.code}
                         className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-xs ${checked ? 'border-primary bg-primary/5' : ''}`}>
                    <Checkbox checked={checked}
                              data-testid={`ec-suite-${s.code}`}
                              onCheckedChange={() => toggleSuite(s.code)} />
                    <span>{s.label}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="ec-global-ethics-note">
              {t('clientProfile.globalEthicsNote', { defaultValue: 'Global Ethics is provisioned separately through per-company anonymous access codes and is not managed here.' })}
            </p>
          </div>

          {/* Sprint Governance — advisor changes to governed fields require
              approval + justification. */}
          {isAdvisor && (
            <div className="p-3 rounded-lg bg-warning/5 border border-warning/30 space-y-2" data-testid="governance-notice">
              <p className="text-xs flex items-center gap-1.5 text-warning font-medium">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                {t('governance.advisorNotice', { defaultValue: 'These configuration changes require HM Admin approval before they take effect.' })}
              </p>
              <div>
                <Label className="text-xs">{t('governance.reasonLabel', { defaultValue: 'Justification' })} *</Label>
                <Textarea
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  placeholder={t('governance.reasonPh', { defaultValue: 'Why is this configuration change needed?' })}
                  rows={2}
                  data-testid="governance-reason"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button
            onClick={save}
            disabled={saving || (governedChanged && !changeReason.trim())}
            data-testid="ec-save"
            className="bg-gradient-primary"
          >
            {saving
              ? t('common.saving', { defaultValue: 'Saving…' })
              : isAdvisor
                ? t('governance.submitForApproval', { defaultValue: 'Submit for approval' })
                : t('common.save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
